<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { boardActions } from "@/action/boardActions";
import ObjectivesList from "@/components/objectives/ObjectivesList.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { applyFullBoardRect } from "@/components/utils/boardRect";
import type { BoardWithObjectives } from "@/model/board";
import type { Team } from "@/model/session";
import { useArtStore } from "@/store/art";
import { useBoardsStore } from "@/store/boards";
import { useTeamStore } from "@/store/team";

import { useBaseBoard } from "./internal/useBaseBoard";

const objectivesBoardRef = useTemplateRef("objectivesBoard");

const { t } = useI18n();

defineExpose({ focus: () => void objectivesBoardRef.value?.focus() });

const board = computed(() => useBoardsStore().boardByType("objectives"));
useBaseBoard(board, applyFullBoardRect);

const programBoard = computed(() => useBoardsStore().boardByType("program"));
const programObjectivesCount = computed(() => objectivesCount(programBoard.value));
const programObjectivesTitle = computed(() =>
  t("objectivesBoard.objectives", { name: useArtStore().current.name }),
);

const currentTeam = computed(() => useTeamStore().current);
const selectedTeamBoard = computed(() => getTeamBoard(currentTeam.value));
const teams = computed(() => useTeamStore().teamsInCurrentArt);
const teamObjectivesCount = computed(() => getTeamObjectivesCount(currentTeam.value));
const teamObjectivesTitle = computed(() =>
  t("objectivesBoard.objectives", { name: currentTeam.value.name }),
);
const currentTeamIndex = computed(() =>
  teams.value.findIndex((t) => t.id === currentTeam.value.id),
);
const isNextDisabled = computed(() => currentTeamIndex.value === teams.value.length - 1);
const isPrevDisabled = computed(() => currentTeamIndex.value === 0);

function getTeamObjectivesCount(team: Team) {
  return objectivesCount(getTeamBoard(team));
}

function objectivesCount(board: BoardWithObjectives) {
  return board.objectives.length + board.stretchObjectives.length;
}

function getTeamBoard(team: Team) {
  return useBoardsStore().boardByType("team", { teamId: team.id });
}

function select(team: Team) {
  boardActions.switchTeam("board", team);
}

function shiftTeam(diff: number) {
  const newIndex = currentTeamIndex.value + diff;
  if (newIndex >= 0 && newIndex < teams.value.length) {
    boardActions.switchTeam("board", teams.value[newIndex]);
  }
}
</script>

<template>
  <div :id="board.id" class="board program-objectives-board">
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
    <div
      ref="objectivesBoard"
      class="objectives-board"
      tabindex="0"
      :aria-label="$t('label.objective.board')"
      @click="objectivesBoardRef?.blur()"
    >
      <section class="column program-objectives" aria-labelledby="program-objectives-title">
        <div class="column-header">
          <div class="team-info">
            <h2 id="program-objectives-title">{{ programObjectivesTitle }}</h2>
            <div class="objectives-count">
              {{ $t("objectives.count", programObjectivesCount) }}
            </div>
          </div>
        </div>
        <div class="scrollable">
          <ObjectivesList group="program-objectives" :board="programBoard" no-status-distribution />
        </div>
      </section>
      <section class="column program-objectives" :aria-label="$t('label.objective.team')">
        <div class="column-header">
          <div class="team-info">
            <DropdownMenu style="height: 40px" class="select-team">
              <template #trigger="{ isOpen }">
                <BaseButton
                  data-dropdown-trigger
                  variant="ghost"
                  color="grey"
                  icon-after="arrow/chevron-down"
                  :aria-label="`${teamObjectivesTitle}: ${$t('page.team')}`"
                  :aria-expanded="isOpen"
                  aria-controls="team-list"
                  aria-haspopup="listbox"
                >
                  {{ teamObjectivesTitle }}
                </BaseButton>
              </template>
              <ComplexList
                id="team-list"
                v-keyboard-nav
                class="dropdown-scroll-container"
                role="listbox"
              >
                <ListItem
                  v-for="team in teams"
                  :key="team.id"
                  role="option"
                  :active="team.id === currentTeam.id"
                  clickable
                  @click="select(team)"
                >
                  <template #default>
                    {{ team.name }}
                  </template>
                  <template #subtitle>
                    {{ $t("objectives.count", getTeamObjectivesCount(team)) }}
                  </template>
                </ListItem>
              </ComplexList>
            </DropdownMenu>
            <div class="objectives-count">
              {{ $t("objectives.count", teamObjectivesCount) }}
            </div>
          </div>

          <span class="arrows-container">
            <IconButton
              variant="outlined"
              size="small"
              icon="arrow/chevron-left"
              :aria-label="$t('label.objective.previousTeam')"
              :text="$t('action.previous')"
              :disabled="isPrevDisabled"
              @click="shiftTeam(-1)"
            />
            <IconButton
              variant="outlined"
              size="small"
              icon="arrow/chevron-right"
              :aria-label="$t('label.objective.nextTeam')"
              :text="$t('action.next')"
              :disabled="isNextDisabled"
              @click="shiftTeam(1)"
            />
          </span>
        </div>
        <div class="scrollable">
          <Transition name="faster-fade" mode="out-in">
            <ObjectivesList :key="selectedTeamBoard.id" :board="selectedTeamBoard" />
          </Transition>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.program-objectives-board {
  background: colors.$background-ui;
  transform: scale(10);
  transform-origin: top left;
  height: 100%;
  width: 100%;
  padding-top: 48px;
  font-size: 100%;
}

.objectives-board {
  display: flex;
  width: 100%;
  height: 100%;

  &:focus-visible {
    outline: 3px solid colors.$outline;
    outline-offset: -3px;
    border-radius: 4px;
  }

  .column {
    display: flex;
    flex-direction: column;
    width: 50vw;

    &.program-objectives {
      border-right: 1px solid colors-a11y.$divider-color;
    }

    .column-header {
      border-bottom: 1px solid colors-a11y.$divider-color;
      padding: 1rem 5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;

      h2 {
        font-size: font.$size-normal;
        font-weight: font.$weight-bold;
        padding: 10px;
        line-height: 20px;
      }

      .objectives-count {
        margin: 0 0 10px 10px;
        font-size: font.$size-small;
        font-weight: font.$weight-semi-bold;
        color: colors.$text-subtle;
      }

      .arrows-container {
        display: flex;
        gap: 0.4rem;
      }
    }

    .scrollable {
      position: relative;
      flex: 1;
      min-height: 0;
      padding-left: 4rem;
      padding-right: 4rem;
    }
  }
}

.dropdown-scroll-container {
  position: relative;
  min-width: 250px;
}
</style>
